import { useCallback, useState } from "react";
import { musicThemes, skeetThemes } from "./theme-suggestions";
import {
  nextStart,
  planRounds,
  formatDeadline,
  localStart,
  type RoundDraft,
} from "./round-planning";
export function useRoundPlan(initial?: {
  drafts: RoundDraft[];
  deadline: string;
  submissionDays: number;
  votingDays: number;
}) {
  const [drafts, setDrafts] = useState<RoundDraft[]>(
    () => initial?.drafts ?? [{ theme: "", description: "" }],
  );
  const [deadline, setDeadline] = useState(
    () => initial?.deadline ?? nextStart(),
  );
  const [submissionDays, setSubmissionDays] = useState(
    initial?.submissionDays ?? 5,
  );
  const [votingDays, setVotingDays] = useState(initial?.votingDays ?? 2);
  const reset = useCallback(() => {
    setDrafts([{ theme: "", description: "" }]);
    setDeadline(nextStart());
    setSubmissionDays(5);
    setVotingDays(2);
  }, []);
  return {
    drafts,
    setDrafts,
    deadline,
    setDeadline,
    submissionDays,
    setSubmissionDays,
    votingDays,
    setVotingDays,
    reset,
    plans: () => planRounds(drafts, deadline, submissionDays, votingDays),
  };
}
export function RoundPlanFields({
  plan,
  testType,
  busy,
  offset = 0,
  anchor,
}: {
  plan: ReturnType<typeof useRoundPlan>;
  testType: string;
  busy: boolean;
  offset?: number;
  anchor?: number;
}) {
  const {
    drafts,
    setDrafts,
    deadline,
    setDeadline,
    submissionDays,
    setSubmissionDays,
    votingDays,
    setVotingDays,
  } = plan;
  const pool = testType === "music" ? musicThemes : skeetThemes;
  return (
    <fieldset className="round-plan-fields" disabled={busy}>
      <legend>Rounds</legend>
      {drafts.map((draft, index) => (
        <fieldset className="round-draft" key={index}>
          <legend>Round {offset + index + 1}</legend>
          <label>
            Theme
            <span className="theme-entry">
              <input
                value={draft.theme}
                maxLength={200}
                required
                onChange={(e) =>
                  setDrafts(
                    drafts.map((d, i) =>
                      i === index ? { ...d, theme: e.target.value } : d,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="theme-sparkle secondary"
                aria-label={`Suggest a theme for round ${offset + index + 1}`}
                title="Suggest a theme"
                onClick={() => {
                  const unused = pool.filter(
                    (theme) => !drafts.some((d) => d.theme === theme),
                  );
                  const choices = unused.length ? unused : pool;
                  const theme =
                    choices[Math.floor(Math.random() * choices.length)];
                  setDrafts(
                    drafts.map((d, i) =>
                      i === index
                        ? {
                            theme,
                            description:
                              testType === "music"
                                ? `Submit one song for “${theme}”.`
                                : `Submit one Bluesky post for “${theme}”.`,
                          }
                        : d,
                    ),
                  );
                }}
              >
                ✨
              </button>
            </span>
          </label>
          <label>
            Description <span className="muted">(optional)</span>
            <textarea
              value={draft.description}
              maxLength={1000}
              rows={2}
              onChange={(e) =>
                setDrafts(
                  drafts.map((d, i) =>
                    i === index ? { ...d, description: e.target.value } : d,
                  ),
                )
              }
            />
          </label>
          {drafts.length > 1 && (
            <button
              type="button"
              className="secondary"
              onClick={() => setDrafts(drafts.filter((_, i) => i !== index))}
            >
              Remove round
            </button>
          )}
        </fieldset>
      ))}
      <button
        type="button"
        className="secondary"
        disabled={drafts.length + offset >= 20}
        onClick={() => setDrafts([...drafts, { theme: "", description: "" }])}
      >
        Add round
      </button>
      <div className="round-timing">
        <label>
          First submission due
          <input
            type="datetime-local"
            required
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </label>
        <span className="muted">
          {Intl.DateTimeFormat().resolvedOptions().timeZone}
        </span>
        <div className="day-fields">
          <label>
            Days to submit
            <input
              type="number"
              min={1}
              max={14}
              step={1}
              required
              value={submissionDays}
              onChange={(e) => {
                const days = Number(e.target.value);
                setSubmissionDays(days);
                if (Number.isInteger(days) && days >= 1 && days <= 14) {
                  const due = new Date(anchor ?? Date.now());
                  due.setDate(due.getDate() + days);
                  setDeadline(localStart(due));
                }
              }}
            />
          </label>
          <label>
            Days to vote
            <input
              type="number"
              min={1}
              max={14}
              step={1}
              required
              value={votingDays}
              onChange={(e) => setVotingDays(Number(e.target.value))}
            />
          </label>
        </div>
      </div>
      <section aria-label="Schedule preview" aria-live="polite">
        <h4>Deadlines</h4>
        <ol className="schedule-preview">
          {plan.plans().map((round, i) => (
            <li key={i}>
              <strong>Round {offset + i + 1}</strong>
              <span>
                Submissions due: {formatDeadline(round.submissionEndsAt)}
              </span>
              <span>Votes due: {formatDeadline(round.votingEndsAt)}</span>
            </li>
          ))}
        </ol>
      </section>
    </fieldset>
  );
}
